import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, ArrowUp, ArrowDown, Eye, Rocket, ArrowClockwise, Image as ImageIcon, X, PencilSimple, Check } from '@phosphor-icons/react';
import MediaPickerInput from '../MediaPickerInput';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_TYPES = [
  'featured_authors', 'popular_authors', 'new_authors',
  'featured_works', 'trending_works', 'most_read_works', 'recently_added',
  'featured_poems', 'featured_stories', 'featured_quotes', 'featured_books', 'featured_articles',
  'poem_of_day', 'story_of_day', 'quote_of_day', 'book_of_day', 'author_of_day',
  'editors_choice', 'featured_categories', 'collections', 'newsletter',
];
const LAYOUTS = ['grid', 'list', 'carousel', 'single', 'banner'];
const SUB_TABS = ['Hero', 'Sections', 'Collections', 'Newsletter', 'Footer', 'SEO'];

export default function CmsHomepage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState('Hero');
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/cms/homepage?which=draft`, { withCredentials: true });
      setConfig(data);
      setDirty(false);
    } catch { toast.error('Failed to load homepage config'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePatch = async (sectionKey, data) => {
    try {
      await axios.patch(`${API}/api/admin/cms/homepage/${sectionKey}`, { data }, { withCredentials: true });
      toast.success(`${sectionKey} saved to draft`);
      setDirty(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const publish = async () => {
    if (!confirm('Publish current draft to live homepage?')) return;
    try {
      await axios.post(`${API}/api/admin/cms/homepage/publish`, {}, { withCredentials: true });
      toast.success('Homepage published!');
      setDirty(false);
    } catch (e) { toast.error('Publish failed'); }
  };

  const resetDraft = async () => {
    if (!confirm('Discard draft changes and copy the published version back to draft?')) return;
    try {
      await axios.post(`${API}/api/admin/cms/homepage/reset-draft`, {}, { withCredentials: true });
      toast.success('Draft reset to published');
      load();
    } catch { toast.error('Reset failed'); }
  };

  if (loading || !config) return <p className="text-center py-16 text-muted-foreground">Loading homepage config…</p>;

  return (
    <div data-testid="cms-homepage" className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>Homepage</h2>
          <p className="text-sm text-muted-foreground">Everything visible on the homepage — hero, sections, collections, footer, and SEO.</p>
          {dirty && <p className="text-xs text-amber-600 mt-1">Unsaved draft changes — click Publish to go live.</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href="/?preview=1" target="_blank" rel="noopener" className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-full text-xs hover:bg-muted" data-testid="hp-preview"><Eye size={14} /> Preview</a>
          <button onClick={resetDraft} className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-full text-xs hover:bg-muted" data-testid="hp-reset-draft"><ArrowClockwise size={14} /> Discard draft</button>
          <button onClick={publish} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-xs font-semibold hover:opacity-90" data-testid="hp-publish"><Rocket size={14} weight="fill" /> Publish</button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border overflow-x-auto">
        {SUB_TABS.map((t) => (
          <button key={t} onClick={() => setSub(t)} data-testid={`hp-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${sub === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{t}</button>
        ))}
      </div>

      {sub === 'Hero' && <HeroEditor slides={config.hero?.slides || []} onSave={(slides) => savePatch('hero', { slides })} />}
      {sub === 'Sections' && <SectionsEditor sections={config.sections || []} onSave={(sections) => savePatch('sections', sections)} />}
      {sub === 'Collections' && <CollectionsEditor collections={config.collections || []} onSave={(c) => savePatch('collections', c)} />}
      {sub === 'Newsletter' && <NewsletterEditor data={config.newsletter || {}} onSave={(d) => savePatch('newsletter', d)} />}
      {sub === 'Footer' && <FooterEditor data={config.footer || {}} onSave={(d) => savePatch('footer', d)} />}
      {sub === 'SEO' && <SeoEditor data={config.seo || {}} onSave={(d) => savePatch('seo', d)} />}
    </div>
  );
}

/* ---------- Hero ---------- */
function HeroEditor({ slides, onSave }) {
  const [items, setItems] = useState(slides);
  useEffect(() => setItems(slides), [slides]);

  const upd = (i, patch) => setItems((c) => c.map((s, idx) => idx === i ? { ...s, ...patch } : c));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    arr.forEach((s, idx) => s.order = idx);
    setItems(arr);
  };
  const add = () => setItems([...items, {
    id: crypto.randomUUID(), title: 'New slide', subtitle: '', description: '',
    image_desktop: '', image_mobile: '', cta_text: 'Learn more', cta_link: '/', is_published: true, order: items.length,
  }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} slides</p>
        <div className="flex gap-2">
          <button onClick={add} className="inline-flex items-center gap-1 text-xs bg-muted hover:bg-muted/70 px-3 py-1.5 rounded-full" data-testid="hero-add-slide"><Plus size={12} /> Add slide</button>
          <button onClick={() => onSave(items)} data-testid="hero-save" className="inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-full"><Check size={12} /> Save</button>
        </div>
      </div>
      {items.map((s, i) => (
        <div key={s.id} className="bg-card border border-border rounded-lg p-4" data-testid={`hero-slide-${i}`}>
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30 hover:bg-muted rounded"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 disabled:opacity-30 hover:bg-muted rounded"><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
              <Text label="Title" value={s.title} onChange={(v) => upd(i, { title: v })} />
              <Text label="Subtitle" value={s.subtitle} onChange={(v) => upd(i, { subtitle: v })} />
              <TextArea className="md:col-span-2" label="Description" value={s.description} onChange={(v) => upd(i, { description: v })} />
              <MediaPickerInput 
                label="Desktop Image" 
                value={s.image_desktop} 
                onChange={(v) => upd(i, { image_desktop: v })} 
                accept="image"
                helpText="Recommended: 1920x800px"
              />
              <MediaPickerInput 
                label="Mobile Image" 
                value={s.image_mobile} 
                onChange={(v) => upd(i, { image_mobile: v })} 
                accept="image"
                helpText="Recommended: 800x600px"
              />
              <Text label="CTA text" value={s.cta_text} onChange={(v) => upd(i, { cta_text: v })} />
              <Text label="CTA link" value={s.cta_link} onChange={(v) => upd(i, { cta_link: v })} />
              <label className="text-xs flex items-center gap-2 mt-2 md:col-span-2">
                <input type="checkbox" checked={!!s.is_published} onChange={(e) => upd(i, { is_published: e.target.checked })} />
                Published (visible on live homepage)
              </label>
            </div>
            <button onClick={() => remove(i)} className="text-red-600 p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 rounded" data-testid={`hero-remove-${i}`}><Trash size={14} /></button>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-center py-8 text-muted-foreground">No slides yet. Click Add slide.</p>}
    </div>
  );
}

/* ---------- Sections ---------- */
function SectionsEditor({ sections, onSave }) {
  const [items, setItems] = useState(sections);
  useEffect(() => setItems([...sections].sort((a,b)=>a.order-b.order)), [sections]);

  const upd = (i, patch) => setItems((c) => c.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    arr.forEach((s, idx) => s.order = idx);
    setItems(arr);
  };
  const add = () => setItems([...items, {
    id: crypto.randomUUID(), type: 'featured_authors', title: 'New section', description: '',
    enabled: true, count: 6, layout: 'grid', auto_populate: true, manual_items: [], view_all_link: '', order: items.length,
  }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} sections</p>
        <div className="flex gap-2">
          <button onClick={add} className="inline-flex items-center gap-1 text-xs bg-muted hover:bg-muted/70 px-3 py-1.5 rounded-full"><Plus size={12} /> Add section</button>
          <button onClick={() => onSave(items)} data-testid="sections-save" className="inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-full"><Check size={12} /> Save</button>
        </div>
      </div>
      {items.map((s, i) => (
        <div key={s.id} className={`bg-card border ${s.enabled ? 'border-border' : 'border-dashed border-muted-foreground/30 opacity-70'} rounded-lg p-4`} data-testid={`section-item-${i}`}>
          <div className="flex items-start gap-3">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30 hover:bg-muted rounded"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 disabled:opacity-30 hover:bg-muted rounded"><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
              <Select label="Type" value={s.type} onChange={(v) => upd(i, { type: v })} options={SECTION_TYPES} />
              <Text label="Heading" value={s.title} onChange={(v) => upd(i, { title: v })} />
              <Text className="md:col-span-2" label="Description" value={s.description} onChange={(v) => upd(i, { description: v })} />
              <Select label="Layout" value={s.layout} onChange={(v) => upd(i, { layout: v })} options={LAYOUTS} />
              <Text label="Item count" type="number" value={s.count} onChange={(v) => upd(i, { count: Number(v) || 0 })} />
              <Text label='"View all" link' value={s.view_all_link} onChange={(v) => upd(i, { view_all_link: v })} />
              <div className="flex flex-col gap-1 text-xs">
                <label className="inline-flex items-center gap-2 mt-4">
                  <input type="checkbox" checked={!!s.enabled} onChange={(e) => upd(i, { enabled: e.target.checked })} data-testid={`sec-${i}-enabled`} /> Enabled
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={!!s.auto_populate} onChange={(e) => upd(i, { auto_populate: e.target.checked })} /> Auto-populate
                </label>
              </div>
              {!s.auto_populate && (
                <div className="md:col-span-4">
                  <Text label="Manual item IDs (comma separated: author_id or work_id)" value={(s.manual_items || []).join(',')} onChange={(v) => upd(i, { manual_items: v.split(',').map(x => x.trim()).filter(Boolean) })} />
                </div>
              )}
            </div>
            <button onClick={() => remove(i)} className="text-red-600 p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 rounded"><Trash size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Collections ---------- */
function CollectionsEditor({ collections, onSave }) {
  const [items, setItems] = useState(collections);
  useEffect(() => setItems(collections), [collections]);
  const upd = (i, patch) => setItems((c) => c.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const add = () => setItems([...items, { id: crypto.randomUUID(), title: 'New Collection', description: '', cover_image: '', work_ids: [], author_ids: [], is_published: true }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} custom collections</p>
        <div className="flex gap-2">
          <button onClick={add} className="inline-flex items-center gap-1 text-xs bg-muted px-3 py-1.5 rounded-full"><Plus size={12} /> Add collection</button>
          <button onClick={() => onSave(items)} data-testid="collections-save" className="inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-full"><Check size={12} /> Save</button>
        </div>
      </div>
      {items.map((c, i) => (
        <div key={c.id} className="bg-card border border-border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Text label="Title" value={c.title} onChange={(v) => upd(i, { title: v })} />
            <Text label="Cover image URL" value={c.cover_image} onChange={(v) => upd(i, { cover_image: v })} />
            <TextArea className="md:col-span-2" label="Description" value={c.description} onChange={(v) => upd(i, { description: v })} />
            <Text label="Work IDs (comma sep)" value={(c.work_ids || []).join(',')} onChange={(v) => upd(i, { work_ids: v.split(',').map(x=>x.trim()).filter(Boolean) })} />
            <Text label="Author IDs (comma sep)" value={(c.author_ids || []).join(',')} onChange={(v) => upd(i, { author_ids: v.split(',').map(x=>x.trim()).filter(Boolean) })} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={!!c.is_published} onChange={(e) => upd(i, { is_published: e.target.checked })} /> Published</label>
            <button onClick={() => remove(i)} className="text-red-600 text-xs inline-flex items-center gap-1"><Trash size={12} /> Delete</button>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-center py-8 text-muted-foreground">No collections yet.</p>}
    </div>
  );
}

/* ---------- Newsletter ---------- */
function NewsletterEditor({ data, onSave }) {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  return (
    <div className="bg-card border border-border rounded-lg p-5 max-w-2xl space-y-3">
      <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={!!d.enabled} onChange={(e) => setD({ ...d, enabled: e.target.checked })} data-testid="newsletter-enabled" /> Section enabled</label>
      <Text label="Heading" value={d.heading} onChange={(v) => setD({ ...d, heading: v })} />
      <TextArea label="Description" value={d.description} onChange={(v) => setD({ ...d, description: v })} />
      <Text label="Background image URL" value={d.background_image} onChange={(v) => setD({ ...d, background_image: v })} />
      <div className="grid grid-cols-2 gap-3">
        <Text label="CTA text" value={d.cta_text} onChange={(v) => setD({ ...d, cta_text: v })} />
        <Text label="CTA link" value={d.cta_link} onChange={(v) => setD({ ...d, cta_link: v })} />
      </div>
      <button onClick={() => onSave(d)} className="bg-primary text-primary-foreground rounded-full px-5 py-2 text-sm font-semibold" data-testid="newsletter-save">Save</button>
    </div>
  );
}

/* ---------- Footer ---------- */
function FooterEditor({ data, onSave }) {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  const upd = (k, v) => setD({ ...d, [k]: v });
  const updList = (k, list) => setD({ ...d, [k]: list });

  const editRow = (list, i, patch) => list.map((r, idx) => idx === i ? { ...r, ...patch } : r);
  const addRow = (k, row) => updList(k, [...(d[k] || []), row]);
  const removeRow = (k, i) => updList(k, (d[k] || []).filter((_, idx) => idx !== i));

  return (
    <div className="bg-card border border-border rounded-lg p-5 max-w-3xl space-y-4">
      <Text label="Logo URL" value={d.logo_url} onChange={(v) => upd('logo_url', v)} />
      <TextArea label="Description" value={d.description} onChange={(v) => upd('description', v)} />

      <div>
        <p className="text-xs uppercase text-muted-foreground mb-1">Quick Links</p>
        {(d.quick_links || []).map((r, i) => (
          <div key={i} className="flex gap-2 mb-1">
            <input value={r.label} onChange={(e) => updList('quick_links', editRow(d.quick_links, i, { label: e.target.value }))} placeholder="Label" className="input flex-1" />
            <input value={r.href} onChange={(e) => updList('quick_links', editRow(d.quick_links, i, { href: e.target.value }))} placeholder="/path" className="input flex-1" />
            <button onClick={() => removeRow('quick_links', i)} className="text-red-600 p-1"><X size={14} /></button>
          </div>
        ))}
        <button onClick={() => addRow('quick_links', { label: '', href: '' })} className="text-xs text-primary">+ Add link</button>
      </div>

      <div>
        <p className="text-xs uppercase text-muted-foreground mb-1">Social Links</p>
        {(d.social_links || []).map((r, i) => (
          <div key={i} className="flex gap-2 mb-1">
            <input value={r.platform} onChange={(e) => updList('social_links', editRow(d.social_links, i, { platform: e.target.value }))} placeholder="Platform (twitter, ig...)" className="input flex-1" />
            <input value={r.url} onChange={(e) => updList('social_links', editRow(d.social_links, i, { url: e.target.value }))} placeholder="https://…" className="input flex-1" />
            <button onClick={() => removeRow('social_links', i)} className="text-red-600 p-1"><X size={14} /></button>
          </div>
        ))}
        <button onClick={() => addRow('social_links', { platform: '', url: '' })} className="text-xs text-primary">+ Add social</button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Text label="Contact email" value={d.contact_email} onChange={(v) => upd('contact_email', v)} />
        <Text label="Copyright" value={d.copyright} onChange={(v) => upd('copyright', v)} />
        <Text label="Privacy policy URL" value={d.privacy_url} onChange={(v) => upd('privacy_url', v)} />
        <Text label="Terms URL" value={d.terms_url} onChange={(v) => upd('terms_url', v)} />
      </div>

      <button onClick={() => onSave(d)} className="bg-primary text-primary-foreground rounded-full px-5 py-2 text-sm font-semibold" data-testid="footer-save">Save</button>
      <style>{`.input{padding:0.5rem 0.75rem;background:hsl(var(--muted)/0.4);border:1px solid transparent;border-radius:0.5rem;font-size:0.875rem;outline:none}.input:focus{border-color:hsl(var(--primary));background:hsl(var(--background))}`}</style>
    </div>
  );
}

/* ---------- SEO ---------- */
function SeoEditor({ data, onSave }) {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  return (
    <div className="bg-card border border-border rounded-lg p-5 max-w-2xl space-y-3">
      <Text label="Meta title" value={d.meta_title} onChange={(v) => setD({ ...d, meta_title: v })} />
      <TextArea label="Meta description" value={d.meta_description} onChange={(v) => setD({ ...d, meta_description: v })} />
      <Text label="Meta keywords (comma sep)" value={d.meta_keywords} onChange={(v) => setD({ ...d, meta_keywords: v })} />
      <Text label="OG image URL" value={d.og_image} onChange={(v) => setD({ ...d, og_image: v })} />
      <Text label="Canonical URL" value={d.canonical_url} onChange={(v) => setD({ ...d, canonical_url: v })} />
      <button onClick={() => onSave(d)} className="bg-primary text-primary-foreground rounded-full px-5 py-2 text-sm font-semibold" data-testid="seo-save">Save</button>
    </div>
  );
}

/* ---------- Small inputs ---------- */
function Text({ label, value, onChange, type = 'text', className = '' }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none" />
    </div>
  );
}
function TextArea({ label, value, onChange, className = '' }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      <textarea rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none" />
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
